import {
	ancestorPaths,
	foldName,
	isWithin,
	type NewSyncOp,
	parentPath,
	pathSegments,
} from '@skysa/core';
import Dexie, { type PromiseExtended } from 'dexie';

import type { FileRecord, NoteRecord, NotesDatabase, OpQueueRecord } from './db.js';
import { rowsOf } from './rows.js';
import { isTombstone } from './rowStates.js';

/**
 * The push queue: what the sync engine owes the remote, in the order it has to
 * happen. Every writer in `store/` that changes a note or a notebook calls one
 * of these in the same transaction as the change itself, so a change and the
 * op that carries it land together or not at all. Pulls never do — what a pull
 * writes came from the remote.
 *
 * The engine reads the queue once, asks for each op again just before sending
 * it — so one withdrawn here in the meantime is never sent — and holds it while
 * it is at the network, and the user can go on editing meanwhile. So what is
 * queued is kept to what the engine needs and no more, and every rule below has
 * to hold even for an op that is already on its way:
 *
 * - **A write carries no content.** The engine reads the note when it runs the
 *   op, so one queued write covers every edit made before it runs, and a note
 *   edited again while its write is in flight is left dirty by the store and
 *   owed a fresh write (`settle` in `sync/store.ts`).
 * - **A move says where the note is going, so a second rename replaces it.**
 *   Two moves queued for one note would move the file to the first name and
 *   back again, and a write addressed to the second name would find nothing
 *   and look for a move that explains it — which is the one queued last. If
 *   the replaced move was in flight, the engine's `completeOp` finds it gone,
 *   and the store settles it as the note having moved on.
 * - **A note never pushed is not moved.** There is nothing at the old path; its
 *   write creates the file wherever the note is by then.
 * - **A delete withdraws the note's writes and keeps its moves, and a deleted
 *   note is still moved.** A tombstone owes the remote its delete and nothing
 *   else, which is the rule `queueWrite` applies from the other end; left
 *   queued, the write of a note made and deleted before either op was sent
 *   creates the file for the delete to remove, and another device's note binds
 *   to that file rather than making one (`queueDelete`). Only the queue row
 *   goes: a write already at the network still records the `remoteId` on the
 *   row, which is what the delete needs, and `completeOp`, `failOp` and
 *   `resolveConflict` all tolerate finding the op withdrawn. The moves stay
 *   because the file is still at the name it had, and a deleted note carried
 *   along by a notebook rename can be restored, when its write needs the file
 *   where the note is.
 *
 * Folder renames and deletes go up as the notes inside them moving or being
 * deleted one by one — the engine has no op that moves a whole folder — and an
 * `rmdir` queued behind them removes the directory they left. Ordered that way
 * round because the notes' own ops are what empty it: the engine refuses an
 * `rmdir` over a directory that still holds any file (`runRmdir`).
 *
 * A file beside a note (#187) has ops of its own, by the same rules where a
 * file has a counterpart to them:
 *
 * - **An upload is sent to wherever the row is when it runs**, as a write is,
 *   and a pending file that moves only has its upload's `path` follow it. The
 *   engine never lets an upload hold up what is behind it.
 * - **A `move-file` replaces an earlier one and keeps its origin**, as a move
 *   does, and one in flight that is replaced is settled as the file having
 *   moved on (`settleFileMove` in `sync/store.ts`).
 * - **A `delete-file` withdraws the file's upload and moves.** It is by id, so
 *   it finds the file wherever an earlier move got it to; its `path` is where
 *   the remote has the file, which is the first withdrawn move's origin.
 * - **A file reaches the remote before the note that links it.** `queueWrite`
 *   adds nothing while a write is queued, so a write queued before the file was
 *   added would run first; the writer that adds or carries the file sends
 *   that write to the back (`requeueWriteBehind`), and so does one that finds
 *   the file already there and not up yet. A notebook's move queues its files'
 *   moves ahead of its notes'.
 *
 * What a whole library owes at once — an import, or the rows a bind carries in
 * — is queued notebook by notebook (`queueByNotebook`), so that another device
 * fills in one notebook at a time.
 */

type QueueDb = Pick<NotesDatabase, 'opQueue'>;

/**
 * Failures in a row before an op is left alone and the sync says `blocked`.
 * Enough that an outage has to outlast the scheduler's backoff on its climb to
 * its cap — about ten minutes of failures in a row — before an op is given up
 * on.
 *
 * Here rather than in the scheduler because it is a fact about the queue, and
 * more than the scheduler asks it: `store/unsynced.ts` has to say "these cannot
 * be sent right now" by the same rule the status line does, or the two disagree
 * about the same op.
 */
export const MAX_OP_ATTEMPTS = 8;

/**
 * Whether an op has been given up on for now, and is holding up everything
 * queued behind it. The one statement of the rule: the engine stops a batch at
 * `attempts >= maxAttempts` (`drainOps` in
 * `packages/core/src/sync/push/drain.ts`), the scheduler names the op by it,
 * and what is unsynced is called blocked by it.
 *
 * Never an `rmdir`, because the engine makes the same exception: one that is
 * out of attempts is completed as done and stepped over — it tidies an empty
 * directory away, nothing behind it depends on that, and it is not the user's
 * work. Calling it blocked here would tell the user their notes cannot be sent
 * while the engine is about to send them.
 *
 * An `upload` is the other exception, and only half of one. The engine steps
 * over it too, so it holds nothing up — but it is the only copy of the file,
 * so it is kept, and the run ends `blocked` on its account (`drained` in the
 * engine). So it counts here: the status line and this rule must agree.
 */
export const outOfAttempts = (
	op: Pick<OpQueueRecord, 'op' | 'attempts'>,
	maxAttempts: number = MAX_OP_ATTEMPTS
): boolean => op.op !== 'rmdir' && op.attempts >= maxAttempts;

// Every helper here returns the promise Dexie made rather than being an `async`
// function. They run inside the writers' transactions, often straight after a
// `Dexie.waitFor`, and Dexie follows its own promises through a transaction
// reliably but native ones only for a limited number of hops: two `async`
// helpers awaited one after another in `applyEdit`, behind the digest, lost the
// transaction to a `PrematureCommitError`.
type Queued = PromiseExtended<void>;

/**
 * The ops queued for one note. For *that* note: an id names a note only inside
 * its connection, and another source's note of the same id has a queue of its
 * own, which a write here must not be taken to be covered by, nor a delete here
 * withdraw.
 */
export const opsFor = (
	db: QueueDb,
	note: Pick<NoteRecord, 'connectionId' | 'id'>
): PromiseExtended<OpQueueRecord[]> =>
	db.opQueue
		.where('noteId')
		.equals(note.id)
		.filter((op) => op.connectionId === note.connectionId)
		.toArray();

const seqsOf = (ops: readonly OpQueueRecord[]): number[] =>
	ops.flatMap((op) => (op.seq === undefined ? [] : [op.seq]));

/** The connection's ops about one path, by the `path` index. */
const atPath = (
	db: QueueDb,
	connectionId: string,
	path: string
): PromiseExtended<OpQueueRecord[]> =>
	db.opQueue
		.where('path')
		.equals(path)
		.filter((op) => op.connectionId === connectionId)
		.toArray();

const nothing = (): Queued => Dexie.Promise.resolve();

const add = (db: QueueDb, op: NewSyncOp & { connectionId: string }): Queued =>
	db.opQueue.add({ attempts: 0, queuedAt: Date.now(), ...op }).then(() => undefined);

/**
 * The note's contents have changed. One queued write is enough, however many
 * edits land before it runs. `except` is an op that is finishing, which does not
 * count as still queued.
 */
export const queueWrite = (db: QueueDb, note: NoteRecord, except?: number): Queued =>
	// A tombstone owes the remote its delete and nothing else.
	isTombstone(note)
		? nothing()
		: opsFor(db, note).then((queued) =>
				queued.some((op) => op.op === 'write' && op.seq !== except)
					? undefined
					: add(db, {
							connectionId: note.connectionId,
							op: 'write',
							noteId: note.id,
							path: note.path,
						})
			);

/**
 * The note is now at `note.path`, having been at `from`. Replaces any move
 * already queued for it, keeping that move's origin: the file is still wherever
 * the first move would have taken it from.
 */
export const queueMove = (db: QueueDb, note: NoteRecord, from: string): Queued =>
	opsFor(db, note).then((ops) => {
		const moves = ops.filter((op) => op.op === 'move');
		// Already on its way to where the note is: leave it where it stands in the
		// queue rather than sending it to the back.
		if (moves.length === 1 && moves[0]?.targetPath === note.path) return undefined;
		const origin = moves[0]?.path ?? from;
		// A tombstone is moved too. Its delete would find the file wherever it
		// is, but it can be restored, and its write is then addressed to where
		// the note has got to — which only a move queued for it explains.
		const owed = note.remoteId !== undefined && origin !== note.path;
		return db.opQueue.bulkDelete(seqsOf(moves)).then(() =>
			owed
				? add(db, {
						connectionId: note.connectionId,
						op: 'move',
						noteId: note.id,
						path: origin,
						targetPath: note.path,
					})
				: undefined
		);
	});

/**
 * The note is deleted. Only ever asked once per deletion: `deleteNote` does
 * nothing to a note already deleted, and a notebook's delete passes over them.
 *
 * Any write still queued for it is withdrawn first — it owes the remote its
 * delete and nothing else, which is the same rule `queueWrite` applies to a
 * tombstone from the other end.
 *
 * Without that, a note made and deleted before either op was ever sent still
 * has its write run: the file is created and then removed again. Harmless on
 * its own, and not harmless at all when another device has made a note of the
 * same name meanwhile. The write puts *this* device's file at the path first,
 * the other device's write then binds its note to that file rather than making
 * one, and the delete behind it takes the other device's note away with it —
 * a note nobody deleted, gone from every device. The two-browser soak found it
 * (`apps/web/tests/soak.test.ts`).
 *
 * A write already at the network is withdrawn from the queue only, which is
 * what `settle` handles: the file it creates is still recorded on the row, so
 * this delete has the `remoteId` it needs to remove it.
 */
export const queueDelete = (db: QueueDb, note: NoteRecord): Queued =>
	opsFor(db, note)
		.then((queued) => db.opQueue.bulkDelete(seqsOf(queued.filter((op) => op.op === 'write'))))
		.then(() =>
			add(db, {
				connectionId: note.connectionId,
				op: 'delete',
				noteId: note.id,
				path: note.path,
			})
		);

/**
 * A deleted note is the user's again. A delete still queued is withdrawn — if it
 * was already on its way, the file is gone and the write re-creates it — and the
 * note is owed a write either way.
 */
export const queueRestore = (db: QueueDb, note: NoteRecord): Queued =>
	opsFor(db, note)
		.then((ops) => db.opQueue.bulkDelete(seqsOf(ops.filter((op) => op.op === 'delete'))))
		.then(() => queueWrite(db, note));

/**
 * A notebook that has to exist on the remote even with nothing in it yet.
 *
 * An `rmdir` queued for this path or for one above it is withdrawn: the user
 * has made the notebook again, and the directory it would remove is the one
 * this needs — or holds it. The engine checks for a row at the path as well
 * (`runRmdir`), which covers an `rmdir` already at the network; this keeps one
 * still queued from being sent at all, since it would otherwise be sent after
 * this `mkdir` and undo it.
 */
export const queueMkdir = (db: QueueDb, connectionId: string, path: string): Queued =>
	// By the `path` index, and only the paths that can hold such an `rmdir`:
	// this runs once per notebook a create or a move brings into being, and a
	// device with a long offline backlog would otherwise read the whole queue
	// each time.
	db.opQueue
		.where('path')
		.anyOf([path, ...ancestorPaths(path)])
		.filter((op) => op.connectionId === connectionId && op.op === 'rmdir')
		.toArray()
		.then((stale) =>
			db.opQueue
				.bulkDelete(seqsOf(stale))
				.then(() => atPath(db, connectionId, path))
				.then((queued) =>
					queued.some((op) => op.op === 'mkdir')
						? undefined
						: add(db, { connectionId, op: 'mkdir', path })
				)
		);

/**
 * A notebook gone from this device before its `mkdir` was ever sent. Withdrawn
 * rather than left to go up: sent, it would make a directory on the remote that
 * nothing here will ever remove — the row is gone, so no `rmdir` can be queued
 * for it, and the next pull reports the directory and makes the notebook again,
 * empty. Everything under the path goes with it, for the same reason.
 *
 * The notes' own ops stay: a note in there may have been moved out rather than
 * deleted, and a write that finds no parent makes it (`runOp`).
 */
export const withdrawMkdirs = (db: QueueDb, connectionId: string, path: string): Queued =>
	rowsOf(db.opQueue, connectionId)
		.filter((op) => op.op === 'mkdir' && isWithin(op.path, path))
		.toArray()
		.then((inside) => db.opQueue.bulkDelete(seqsOf(inside)))
		.then(() => undefined);

/**
 * A notebook removed from this device, whose directory the remote still has —
 * deleted, or left behind by a rename. Queued after the ops that empty it, and
 * with the folder's `remoteId` as it was: the row is gone, so nothing else can
 * say which directory this is about, and the engine does nothing without it.
 *
 * Nothing to do for a notebook the remote never had: no id, no directory.
 */
export const queueRmdir = (
	db: QueueDb,
	connectionId: string,
	path: string,
	remoteId: string | undefined
): Queued =>
	remoteId === undefined
		? nothing()
		: atPath(db, connectionId, path).then((queued) =>
				// By path and id together. By the id alone, a folder another
				// device renamed — the same directory at a second path — would
				// find its `rmdir` already queued for a path it has left, where
				// the engine refuses it, and be removed from neither.
				queued.some((op) => op.op === 'rmdir' && op.remoteId === remoteId)
					? undefined
					: add(db, { connectionId, op: 'rmdir', path, remoteId })
			);

/** The ops queued for one file, within its connection, as `opsFor` for a note. */
export const opsForFile = (
	db: QueueDb,
	file: Pick<FileRecord, 'connectionId' | 'id'>
): PromiseExtended<OpQueueRecord[]> =>
	db.opQueue
		.where('fileId')
		.equals(file.id)
		.filter((op) => op.connectionId === file.connectionId)
		.toArray();

/**
 * A file added here, owed its bytes on the remote. The engine reads the bytes
 * when it runs the op, from this device or, for a copy whose bytes it does
 * not hold, from `copyOf` — the `remoteId` of the file it copies.
 *
 * One upload is enough, as one write is for a note: asked again for a file
 * that has one queued — a bind that both resumes rows and finds the same
 * connection's own owed (`bindConnection`) — it adds nothing, and the file is
 * not sent twice.
 */
export const queueUpload = (db: QueueDb, file: FileRecord, copyOf?: string): Queued =>
	opsForFile(db, file).then((queued) =>
		queued.some((op) => op.op === 'upload')
			? undefined
			: add(db, {
					connectionId: file.connectionId,
					op: 'upload',
					fileId: file.id,
					path: file.path,
					...(copyOf === undefined ? {} : { copyOf }),
				})
	);

/**
 * The file is now at `file.path`, having been at `from`. `queueMove`'s rules:
 * an earlier move is replaced and its origin kept, one already going there is
 * left where it stands, and one that would end where it starts is withdrawn.
 *
 * A pending file is not moved — there is nothing at `from` — and its upload
 * goes wherever the row is. Its `path` follows the row all the same, so that
 * what the queue says of the file is where the file is.
 */
export const queueMoveFile = (db: QueueDb, file: FileRecord, from: string): Queued =>
	opsForFile(db, file).then((ops) => {
		if (file.remoteId === undefined) {
			const uploads = ops.filter((op) => op.op === 'upload');
			return db.opQueue
				.bulkPut(uploads.map((op) => ({ ...op, path: file.path })))
				.then(() => undefined);
		}
		const moves = ops.filter((op) => op.op === 'move-file');
		if (moves.length === 1 && moves[0]?.targetPath === file.path) return undefined;
		const origin = moves[0]?.path ?? from;
		return db.opQueue.bulkDelete(seqsOf(moves)).then(() =>
			origin === file.path
				? undefined
				: add(db, {
						connectionId: file.connectionId,
						op: 'move-file',
						fileId: file.id,
						path: origin,
						targetPath: file.path,
					})
		);
	});

/**
 * The file's row is going: its notebook is being deleted. Its upload and its
 * moves are withdrawn — sent, they would put a file on the remote, or move one,
 * that nothing here would ever remove — and a bound one is owed a `delete-file`.
 *
 * By id, which finds the file wherever a move already sent got it to, and from
 * where the remote has it: the origin of a move still queued, which goes with
 * the rest. Without a `fileId`, as the store queues one for an upload whose row
 * went while it was on its way: there is no row left for the op to be about.
 * An upload already on its way is withdrawn from the queue only, and settled by
 * the store as a file deleted since (`settleUpload` in `sync/store.ts`).
 */
export const queueDeleteFile = (db: QueueDb, file: FileRecord): Queued =>
	opsForFile(db, file).then((ops) => {
		const owed = ops.filter((op) => op.op === 'upload' || op.op === 'move-file');
		const at = ops.find((op) => op.op === 'move-file')?.path ?? file.path;
		return db.opQueue.bulkDelete(seqsOf(owed)).then(() =>
			file.remoteId === undefined
				? undefined
				: add(db, {
						connectionId: file.connectionId,
						op: 'delete-file',
						path: at,
						remoteId: file.remoteId,
					})
		);
	});

/**
 * The note's queued write, sent to the back of the queue: behind a file just
 * queued that the note links, so that the file lands first and the note never
 * reaches another device linking a file it cannot find yet. A write carries no
 * content, so moving it costs nothing, and it keeps its attempts. A note with
 * no write queued is owed none for this: the edit that adds the link queues one,
 * behind the file.
 *
 * A write already on its way is withdrawn from the queue and queued again, so
 * the note is sent a second time behind the file; the store settles the first
 * as it does any withdrawn write.
 */
export const requeueWriteBehind = (db: QueueDb, note: NoteRecord): Queued =>
	opsFor(db, note).then((ops) => {
		const writes = ops.filter((op) => op.op === 'write');
		const [first] = writes;
		if (first === undefined) return undefined;
		const { seq: _seq, ...again } = first;
		return db.opQueue
			.bulkDelete(seqsOf(writes))
			.then(() => db.opQueue.add(again))
			.then(() => undefined);
	});

/** What a library owes the remote at once: notebooks to make, files to send, notes to write. */
export interface Owed {
	readonly folders: readonly string[];
	readonly files: readonly FileRecord[];
	readonly notes: readonly NoteRecord[];
}

/** One owed op, by the notebook it is in (folded segments) and which of its ops it is. */
interface InNotebook {
	readonly notebook: readonly string[];
	readonly rank: number;
	readonly queue: () => Queued;
}

const notebookOf = (path: string): string[] => pathSegments(path).map(foldName);

/**
 * Notebooks in depth-first pre-order: each before every notebook inside it, and
 * siblings by name. The top of the source, which is no notebook, is first.
 */
const preOrder = (a: readonly string[], b: readonly string[]): number => {
	const [name, ...insideA] = a;
	const [other, ...insideB] = b;
	if (name === undefined || other === undefined) return a.length - b.length;
	return name.localeCompare(other) || preOrder(insideA, insideB);
};

/**
 * What a whole library owes the remote, queued notebook by notebook: a pick of
 * files imported (`importLibrary`), or the rows a bind or a move carries into a
 * connection (`queueOwed` in `connection.ts`). The notes at the top of the
 * source first, which need no `mkdir`; then each notebook in turn — its
 * `mkdir`, the uploads of the files directly in it, the writes of the notes
 * directly in it — and after it the notebooks inside it, in the same way.
 *
 * So another device fills in a notebook at a time. Queued as they once were,
 * every `mkdir`, then every upload, then every write, a library of a thousand
 * notes on Google Drive, where a note is about four requests one after another,
 * showed every notebook on another device, empty, long before the first note
 * reached any of them.
 *
 * A notebook's `mkdir` comes before everything in it, since `createFolder` is
 * not recursive everywhere. A notebook's files come before its notes, so a
 * picture beside a note lands before the note does. One a note links from
 * elsewhere — a notebook inside, such as `assets/`, or another by `../` — may
 * land after it, and shows as not there until it does, which heals itself.
 *
 * Siblings by name, by `localeCompare`, as paths are sorted elsewhere in the
 * store; folded, so one notebook spelled two ways by two rows is one notebook
 * here as on every provider, and its `mkdir` comes before what is in it in
 * either spelling. Within a notebook, its files, and its notes, keep the order
 * they are given in.
 *
 * Only what is queued here is ordered so. Each op is queued by the helper that
 * dedupes it, so a notebook with a `mkdir` queued, a file with an upload or a
 * note with a write gets nothing more, and the one queued keeps its place.
 */
export const queueByNotebook = (db: QueueDb, connectionId: string, owed: Owed): Queued =>
	[
		...owed.folders.map((path): InNotebook => ({
			notebook: notebookOf(path),
			rank: 0,
			queue: () => queueMkdir(db, connectionId, path),
		})),
		...owed.files.map((file): InNotebook => ({
			notebook: notebookOf(parentPath(file.path)),
			rank: 1,
			queue: () => queueUpload(db, file),
		})),
		...owed.notes.map((note): InNotebook => ({
			notebook: notebookOf(parentPath(note.path)),
			rank: 2,
			queue: () => queueWrite(db, note),
		})),
	]
		.sort((a, b) => preOrder(a.notebook, b.notebook) || a.rank - b.rank)
		.reduce<Queued>((queued, next) => queued.then(next.queue), nothing());
